import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '@nrwl/environment';
import {
  PneInjectable,
  PneInjectionSite,
  PneTreatmentInjection,
  PneTreatmentVitals,
} from '../types/pne.types';

@Injectable({
  providedIn: 'root',
})
export class WidgetApiService {
  private insurancePlansCache$: Observable<any> | null = null;

  constructor(private readonly _http: HttpClient) {}

  public getHealthGorillaEnabled(): Observable<any> {
    return this._http.get(environment.baseAPIUrlMicro + 'egress-proxy/pne/tenantaddon');
  }

  public getInsurancePlansPage(
    page: number = 1,
    rows: number = 100,
    searchTerm?: string,
  ): Observable<any> {
    let url = `${environment.baseAPIUrlMicro}egress-proxy/pne/insurancepayers?Page=${page}&Rows=${rows}`;

    if (searchTerm && searchTerm.trim()) {
      url += `&name=${encodeURIComponent(searchTerm.trim())}`;
    }

    console.log(
      `Fetching insurance plans page ${page}${searchTerm ? ` with search: "${searchTerm}"` : ''}`,
    );

    return this._http.get(url).pipe(
      map((response) => {
        // Handle both array and object response formats
        if (Array.isArray(response)) {
          return {
            data: response,
            totalCount: response.length,
            hasMore: response.length >= rows,
          };
        }

        const data = (response as any).Data || (response as any).data || [];
        const totalCount =
          (response as any).TotalCount ||
          (response as any).totalCount ||
          (response as any).Total ||
          data.length;

        return {
          data: data,
          totalCount: totalCount,
          hasMore: data.length >= rows,
        };
      }),
      catchError((error) => {
        console.error(`Error fetching insurance plans page ${page}:`, error);
        return of({ data: [], totalCount: 0, hasMore: false });
      }),
    );
  }

  public getInsurancePlanByPayerId(payerId: string): Observable<any> {
    const url = `${environment.baseAPIUrlMicro}egress-proxy/pne/insurancepayers?PayerId=${encodeURIComponent(
      payerId,
    )}`;
    console.log(`Fetching insurance plan by PayerId: "${payerId}"`);

    return this._http.get(url).pipe(
      map((response) => {
        if (Array.isArray(response)) {
          return response.length > 0 ? response[0] : null;
        }

        const data = (response as any).Data || (response as any).data || [];
        return Array.isArray(data) && data.length > 0 ? data[0] : null;
      }),
      catchError((error) => {
        console.error(`Error fetching insurance plan by PayerId ${payerId}:`, error);
        return of(null);
      }),
    );
  }

  public clearInsurancePlansCache(): void {
    this.insurancePlansCache$ = null;
  }

  public getCustomerInsurances(params?: {
    Page?: number;
    Rows?: number;
    CustomerId?: string | number;
  }): Observable<any> {
    const queryParams = new URLSearchParams();
    if (params?.Page) queryParams.append('Page', params.Page.toString());
    if (params?.Rows) queryParams.append('Rows', params.Rows.toString());
    if (params?.CustomerId) queryParams.append('CustomerId', params.CustomerId.toString());
    const queryString = queryParams.toString();
    return this._http.get(
      `${environment.baseAPIUrlMicro}egress-proxy/pne/customerinsurance${queryString ? '?' + queryString : ''}`,
    );
  }

  public getCustomerSurgery(params?: {
    Page?: number;
    Rows?: number;
    StartDate?: string;
    EndDate?: string;
    CustomerId?: string | number;
  }): Observable<any> {
    const queryParams = new URLSearchParams();
    if (params?.Page) queryParams.append('Page', params.Page.toString());
    if (params?.Rows) queryParams.append('Rows', params.Rows.toString());
    if (params?.StartDate) queryParams.append('StartDate', params.StartDate);
    if (params?.EndDate) queryParams.append('EndDate', params.EndDate);
    if (params?.CustomerId) queryParams.append('CustomerId', params.CustomerId.toString());
    const queryString = queryParams.toString();
    return this._http.get(
      `${environment.baseAPIUrlMicro}egress-proxy/pne/customersurgery${queryString ? '?' + queryString : ''}`,
    );
  }

  public getCustomerChronicCondition(params?: {
    Page?: number;
    Rows?: number;
    StartDate?: string;
    EndDate?: string;
    CustomerId?: string | number;
  }): Observable<any> {
    const queryParams = new URLSearchParams();
    if (params?.Page) queryParams.append('Page', params.Page.toString());
    if (params?.Rows) queryParams.append('Rows', params.Rows.toString());
    if (params?.StartDate) queryParams.append('StartDate', params.StartDate);
    if (params?.EndDate) queryParams.append('EndDate', params.EndDate);
    if (params?.CustomerId) queryParams.append('CustomerId', params.CustomerId.toString());
    const queryString = queryParams.toString();
    return this._http.get(
      `${environment.baseAPIUrlMicro}egress-proxy/pne/customerchronicconditions${
        queryString ? '?' + queryString : ''
      }`,
    );
  }

  public getCustomerMedication(params?: {
    Page?: number;
    Rows?: number;
    StartDate?: string;
    EndDate?: string;
    CustomerId?: string | number;
  }): Observable<any> {
    const queryParams = new URLSearchParams();
    if (params?.Page) queryParams.append('Page', params.Page.toString());
    if (params?.Rows) queryParams.append('Rows', params.Rows.toString());
    if (params?.StartDate) queryParams.append('StartDate', params.StartDate);
    if (params?.EndDate) queryParams.append('EndDate', params.EndDate);
    if (params?.CustomerId) queryParams.append('CustomerId', params.CustomerId.toString());
    const queryString = queryParams.toString();
    return this._http.get(
      `${environment.baseAPIUrlMicro}egress-proxy/pne/customermedications${queryString ? '?' + queryString : ''}`,
    );
  }

  public getCustomerAllergy(params?: {
    Page?: number;
    Rows?: number;
    StartDate?: string;
    EndDate?: string;
    CustomerId?: string | number;
  }): Observable<any> {
    const queryParams = new URLSearchParams();
    if (params?.Page) queryParams.append('Page', params.Page.toString());
    if (params?.Rows) queryParams.append('Rows', params.Rows.toString());
    if (params?.StartDate) queryParams.append('StartDate', params.StartDate);
    if (params?.EndDate) queryParams.append('EndDate', params.EndDate);
    if (params?.CustomerId) queryParams.append('CustomerId', params.CustomerId.toString());
    const queryString = queryParams.toString();
    return this._http.get(
      `${environment.baseAPIUrlMicro}egress-proxy/pne/customerallergy${queryString ? '?' + queryString : ''}`,
    );
  }

  // ChronicCondition API methods
  public getChronicConditionById(id: string | number): Observable<any> {
    return this._http.get(`${environment.baseAPIUrlMicro}egress-proxy/pne/chronicconditions/${id}`);
  }

  public getChronicConditions(params?: { Page?: number; Rows?: number }): Observable<any> {
    const queryParams = new URLSearchParams();
    if (params?.Page) queryParams.append('Page', params.Page.toString());
    if (params?.Rows) queryParams.append('Rows', params.Rows.toString());
    const queryString = queryParams.toString();
    return this._http.get(
      `${environment.baseAPIUrlMicro}egress-proxy/pne/chronicconditions${queryString ? '?' + queryString : ''}`,
    );
  }

  // Surgery API methods
  public getSurgeryById(id: string | number): Observable<any> {
    return this._http.get(`${environment.baseAPIUrlMicro}egress-proxy/pne/surgeries/${id}`);
  }

  public getSurgeries(params?: { Page?: number; Rows?: number }): Observable<any> {
    const queryParams = new URLSearchParams();
    if (params?.Page) queryParams.append('Page', params.Page.toString());
    if (params?.Rows) queryParams.append('Rows', params.Rows.toString());
    const queryString = queryParams.toString();
    return this._http.get(
      `${environment.baseAPIUrlMicro}egress-proxy/pne/surgeries${queryString ? '?' + queryString : ''}`,
    );
  }

  // Medication API methods
  public getMedicationById(id: string | number): Observable<any> {
    return this._http.get(`${environment.baseAPIUrlMicro}egress-proxy/pne/medication/${id}`);
  }

  public getMedications(params?: { Page?: number; Rows?: number }): Observable<any> {
    const queryParams = new URLSearchParams();
    if (params?.Page) queryParams.append('Page', params.Page.toString());
    if (params?.Rows) queryParams.append('Rows', params.Rows.toString());
    const queryString = queryParams.toString();
    return this._http.get(
      `${environment.baseAPIUrlMicro}egress-proxy/pne/medication${queryString ? '?' + queryString : ''}`,
    );
  }

  // Allergy API methods
  public getAllergyById(id: string | number): Observable<any> {
    return this._http.get(`${environment.baseAPIUrlMicro}egress-proxy/pne/allergies/${id}`);
  }

  public getAllergies(params?: { Page?: number; Rows?: number }): Observable<any> {
    const queryParams = new URLSearchParams();
    if (params?.Page) queryParams.append('Page', params.Page.toString());
    if (params?.Rows) queryParams.append('Rows', params.Rows.toString());
    const queryString = queryParams.toString();
    return this._http.get(
      `${environment.baseAPIUrlMicro}egress-proxy/pne/allergies${queryString ? '?' + queryString : ''}`,
    );
  }

  // Customer API methods
  public getCustomerById(id: string | number): Observable<any> {
    return this._http.get(`${environment.baseAPIUrlMicro}egress-proxy/pne/customers/${id}`);
  }

  public getCustomers(params?: {
    FirstName?: string;
    LastName?: string;
    Page?: number;
    Rows?: number;
    ModifiedOnStart?: string;
    ModifiedOnEnd?: string;
    Email?: string;
    MobilePhone?: string;
  }): Observable<any> {
    const queryParams = new URLSearchParams();
    if (params?.FirstName) queryParams.append('FirstName', params.FirstName);
    if (params?.LastName) queryParams.append('LastName', params.LastName);
    if (params?.Page) queryParams.append('Page', params.Page.toString());
    if (params?.Rows) queryParams.append('Rows', params.Rows.toString());
    if (params?.ModifiedOnStart) queryParams.append('ModifiedOnStart', params.ModifiedOnStart);
    if (params?.ModifiedOnEnd) queryParams.append('ModifiedOnEnd', params.ModifiedOnEnd);
    if (params?.Email) queryParams.append('Email', params.Email);
    if (params?.MobilePhone) queryParams.append('MobilePhone', params.MobilePhone);
    const queryString = queryParams.toString();
    return this._http.get(
      `${environment.baseAPIUrlMicro}egress-proxy/pne/customers${queryString ? '?' + queryString : ''}`,
    );
  }

  public getCompounds(params: { Page?: number; Rows?: number } = {}): Observable<PneInjectable[]> {
    let queryParams = new HttpParams();

    if (params.Page) {
      queryParams = queryParams.append('Page', params.Page);
    }

    if (params.Rows) {
      queryParams = queryParams.append('Rows', params.Rows);
    }

    return this._http.get<PneInjectable[]>(
      `${environment.baseAPIUrlMicro}egress-proxy/pne/injectables`,
      { params: queryParams },
    );
  }

  public getSites(): Observable<PneInjectionSite[]> {
    const url = `${environment.baseAPIUrlMicro}egress-proxy/pne/injectionsites`;

    return this._http.get<PneInjectionSite[]>(url);
  }

  public getCustomerShots(params?: {
    CustomerId?: string | number;
    Page?: number;
    Rows?: number;
    StartDate?: string;
    EndDate?: string;
    Sort?: string;
  }): Observable<PneTreatmentInjection[]> {
    const queryParams = new URLSearchParams();

    if (params?.Page) queryParams.append('Page', params.Page.toString());
    if (params?.Rows) queryParams.append('Rows', params.Rows.toString());
    if (params?.StartDate) queryParams.append('StartDate', params.StartDate);
    if (params?.EndDate) queryParams.append('EndDate', params.EndDate);
    if (params?.CustomerId) queryParams.append('CustomerId', params.CustomerId.toString());
    if (params?.Sort) queryParams.append('Sort', params.Sort);

    const queryString = queryParams.toString();

    return this._http.get<PneTreatmentInjection[]>(
      `${environment.baseAPIUrlMicro}egress-proxy/pne/treatmentinjection${queryString ? '?' + queryString : ''}`,
    );
  }

  public getCustomerVitals(params?: {
    CustomerId?: string | number;
    TreatmentId?: string | number;
    AppointmentId?: string | number;
  }): Observable<PneTreatmentVitals[]> {
    const queryParams = new URLSearchParams();

    if (params?.CustomerId) queryParams.append('CustomerId', params.CustomerId.toString());
    if (params?.TreatmentId) queryParams.append('TreatmentId', params.TreatmentId.toString());
    if (params?.AppointmentId) queryParams.append('AppointmentId', params.AppointmentId.toString());

    const queryString = queryParams.toString();

    return this._http.get<PneTreatmentVitals[]>(
      `${environment.baseAPIUrlMicro}egress-proxy/pne/TreatmentVitals${queryString ? '?' + queryString : ''}`,
    );
  }
}
